import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Shared hook for language ↔ URL synchronization.
 * Used across Dashboard, EditProfile, History, etc.
 *
 * Handles:
 * - Reading `?lang=` from URL on mount
 * - Keeping language state in sync when URL changes
 * - Updating URL when language changes programmatically
 */
export function useLanguageSync() {
    const location = useLocation();
    const navigate = useNavigate();

    const getLangFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        return params.get("lang") === "zh" ? "zh" : "en";
    }, [location.search]);

    const [language, setLanguage] = useState(getLangFromUrl());

    // Sync language when URL changes (e.g. user pastes a new URL)
    useEffect(() => {
        setLanguage(getLangFromUrl());
    }, [getLangFromUrl]);

    // Update URL when language changes programmatically
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("lang") !== language) {
            params.set("lang", language);
            navigate({ search: params.toString() }, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    return { language, setLanguage };
}
